import { isSameDay } from "date-fns"
import { motion } from "motion/react"
import React, { useMemo } from "react"
import styled, { css } from "styled-components"
import { Icon } from "../Icon"
import type { WeekStartsOn } from "../types/dates"
import type { BorderProps } from "../types/props"
import { getBorderStyle } from "../util/props/border"
import { contrastColor, overloadColor } from "../util/style"
import { type Day, DayNameFormat, getDayNames, resolveMonth } from "./util"

interface WrapperProps {
  width: string
}
export const Wrapper = styled.div<WrapperProps>`
  position: relative;
  width: ${(props) => props.width};
`

interface DisplayProps {
  keepOpen: boolean
  focusBorderColor: string
}
export const Display = styled.div<DisplayProps>`
  ${(props) =>
    props.keepOpen &&
    css`
      input {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    `}
`
interface RangeDisplayProps extends DisplayProps {
  borderRadius: string
  separatorColor: string
}
export const RangeDisplay = styled(Display)<RangeDisplayProps>`
  display: grid;
  grid-template-columns: 1fr auto 1fr;

  & > div:first-child input {
    border-right: 0;
  }
  & > div:last-child input {
    border-left: 0;
  }
  .--input-icon-right {
    border-radius: 0 ${(props) => props.borderRadius}
      ${(props) => props.borderRadius} 0;
  }
  .sep {
    width: 1px;
    height: 100%;
    background-color: ${(props) => overloadColor(props.separatorColor)};
  }
  &:focus-within .sep {
    background-color: ${(props) => overloadColor(props.focusBorderColor)};
  }
`

export const CurtainShadow = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  box-shadow: 0 6px 10px 0 rgba(0, 0, 0, 0.1);
  pointer-events: none;
`

interface CurtainProps extends BorderProps {
  keepOpen: boolean
  background: string
}
export const Curtain = styled(motion.div)<CurtainProps>`
  ${(props) =>
    !props.keepOpen &&
    css`
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      z-index: 1;
      will-change: transform, opacity;
    `}

  width: 100%;
  padding: 8px 6px 6px;
  background-color: ${(props) => overloadColor(props.background)};
  ${getBorderStyle}
  border-radius: ${(props) => props.theme.sizes.defaultBorderRadius};

  ${(props) =>
    props.keepOpen &&
    css`
      border-radius: 0 0 ${(props) => props.theme.sizes.defaultBorderRadius}
        ${(props) => props.theme.sizes.defaultBorderRadius};
      border-top: 0;
    `}
`

export const RangeCurtain = styled(motion.div)<CurtainProps>`
  ${(props) =>
    !props.keepOpen &&
    css`
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      z-index: 1;
      will-change: transform, opacity;
    `}

  width: 100%;
  padding: 8px 6px 6px;

  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 8px;

  background-color: ${(props) => overloadColor(props.background)};
  ${getBorderStyle}
  border-radius: ${(props) => props.theme.sizes.defaultBorderRadius};

  ${(props) =>
    props.keepOpen &&
    css`
      border-radius: 0 0 ${(props) => props.theme.sizes.defaultBorderRadius}
        ${(props) => props.theme.sizes.defaultBorderRadius};
      border-top: 0;
    `}
`

export const MonthInfo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 0.8rem;
  text-transform: capitalize;

  span {
    cursor: pointer;

    &:hover {
      color: ${(props) => props.theme.colors.primary};
    }
    &:not(:last-of-type) {
      margin-right: 0.6ch;
    }
  }
`

interface MonthGridProps {
  noWeekNums: boolean
}
export const MonthGrid = styled.div<MonthGridProps>`
  width: 100%;
  margin-top: 8px;

  display: grid;
  grid-template-columns: repeat(
    ${(props) => (props.noWeekNums ? "7" : "8")},
    1fr
  );
`

interface DateBoxProps {
  bold?: boolean
}
export const DateBox = styled.div<DateBoxProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 0;

  font-size: 0.8rem;
  ${(props) => props.bold && "font-weight: bold;"}
  user-select: none;
`
interface DayBoxProps {
  isSelected: boolean
  isToday: boolean
  isSameMonth: boolean
  highlight: string | null
  borderRadius: string | null
  selectedBackground: string
  disableSelected: boolean
}
export const DayBox = styled(DateBox)<DayBoxProps>`
  opacity: ${(props) => (props.isSameMonth ? "1" : "0.5")};
  border: 1px solid rgba(0, 0, 0, 0);

  border-radius: ${(props) => props.borderRadius};

  ${(props) => {
    if (!props.highlight)
      return css`
        &:hover {
          background: ${(props) => props.theme.colors.lightBlue};
        }
      `

    const bg = overloadColor(props.highlight)
    return css`
      background: ${bg};
      color: ${contrastColor(bg)};
    `
  }}

  ${(props) => {
    if (props.disableSelected) return null

    const selColor = overloadColor(props.selectedBackground)
    return (
      props.isSelected &&
      css`
        background: ${selColor};
        color: ${contrastColor(selColor)};

        &:hover {
          background: ${selColor};
        }
      }
    `
    )
  }};

  ${(props) =>
    props.isToday &&
    css`
      border: 1px solid ${props.theme.colors.blueGray};
    `};
`

export const WeekBox = styled(DateBox)`
  opacity: 0.7;
`

export const Year = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, auto);
  gap: 6px;
`
interface MonthBoxProps {
  isCurrent: boolean
}
export const MonthBox = styled.div<MonthBoxProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 36px;

  border: 1px solid
    ${(props) =>
      props.isCurrent ? props.theme.colors.primary : props.theme.colors.gray8};
  border-radius: 6px;
  font-size: 0.7rem;
  text-transform: capitalize;
  user-select: none;
  cursor: pointer;

  &:hover {
    background: linear-gradient(10deg, #9dbccf, #b5d3e6);
  }
`

export const Duodecade = styled(Year)``
export const YearBox = styled(MonthBox)``

export const RangeMonthWrapper = styled.div``
export const RangeMonthInfo = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  padding: 0 6px;
  user-select: none;

  div.--date-range-picker-curtain-month-name {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 0.8rem;
  }
`
interface NavArrowWrapperProps {
  hide: boolean
}
const NavArrowWrapper = styled.div<NavArrowWrapperProps>`
  ${(props) =>
    props.hide &&
    css`
      pointer-events: none;
      touch-action: none;
      visibility: hidden;
    `}
`

/* Functional */

interface NavArrowProps {
  dir: "prev" | "next"
  hide: boolean
  onClick: (dir: "prev" | "next") => void
}
export const NavArrow: React.FC<NavArrowProps> = ({ dir, hide, onClick }) => {
  return (
    <NavArrowWrapper hide={hide}>
      <Icon
        icon={dir === "prev" ? "chevron-left" : "chevron-right"}
        cursor="pointer"
        size="0.8rem"
        hoverColor="primary"
        onClick={() => onClick(dir)}
      />
    </NavArrowWrapper>
  )
}

interface MonthProps {
  value: Date
  secondValue?: Date | null
  month?: Date

  weekStartsOn: WeekStartsOn
  noWeekNums: boolean
  highlight?: (date: Day) => string | null
  selectedBackground?: string
  disableSelected?: boolean

  dateFormatter: (date: Date, format?: string) => string
  onChange: (date: Date) => void
}
export const Month: React.FC<MonthProps> = ({
  value,
  secondValue,
  month,

  weekStartsOn,
  noWeekNums,
  highlight,
  selectedBackground,
  disableSelected,

  dateFormatter,
  onChange,
}) => {
  const allDays = useMemo(
    () => getDayNames(DayNameFormat.VeryShort, dateFormatter, weekStartsOn),
    [dateFormatter, weekStartsOn],
  )

  const currentMonth = useMemo(
    () => resolveMonth(month ?? value, dateFormatter, weekStartsOn),
    [value, month, dateFormatter, weekStartsOn],
  )

  function getBorderRadius(date: Date) {
    if (!secondValue) return

    const side =
      isSameDay(value, secondValue) && isSameDay(value, date)
        ? "both"
        : isSameDay(date, value)
          ? "left"
          : isSameDay(date, secondValue)
            ? "right"
            : null

    return side === "both"
      ? "4px"
      : side === "left"
        ? "4px 0 0 4px"
        : side === "right"
          ? "0 4px 4px 0"
          : "0"
  }

  return (
    <MonthGrid noWeekNums={noWeekNums}>
      {!noWeekNums && <DateBox />}

      {allDays.map((dayName) => (
        <DateBox key={dayName} bold>
          {dayName}
        </DateBox>
      ))}

      {currentMonth.weeks.map((week) => (
        <React.Fragment key={week.num}>
          {!noWeekNums && <WeekBox>{week.num}</WeekBox>}

          {week.days.map((day) => {
            const hlColor = highlight?.(day) ?? null
            const borderRadius = getBorderRadius(day.date) ?? null

            return (
              <DayBox
                key={day.date.getTime()}
                isToday={day.isToday}
                isSelected={isSameDay(day.date, value)}
                isSameMonth={day.date.getMonth() === currentMonth.num}
                highlight={hlColor}
                borderRadius={borderRadius}
                selectedBackground={selectedBackground ?? "primary"}
                disableSelected={!!disableSelected}
                onClick={() => onChange(day.date)}
              >
                {day.num}
              </DayBox>
            )
          })}
        </React.Fragment>
      ))}
    </MonthGrid>
  )
}
